import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocale } from '@parentpath/i18n';
import type {
  BabyDevelopmentCategory,
  BabyDevelopmentContent,
  BabyDevelopmentActivity,
  BabyDevelopmentMedical,
} from '../types';

export interface UsePhaseContentResult {
  categories: BabyDevelopmentCategory[];
  content: BabyDevelopmentContent[];
  activities: BabyDevelopmentActivity[];
  medical: BabyDevelopmentMedical | null;
  isLoading: boolean;
  error: Error | null;
}

export function usePhaseContent(phaseId: string | undefined): UsePhaseContentResult {
  const { locale } = useLocale();

  // Fetch categories (locale-aware)
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['baby-development-categories', locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('baby_development_categories')
        .select('*')
        .eq('locale', locale)
        .order('sort_order');

      if (error) throw error;
      return data as BabyDevelopmentCategory[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour - categories rarely change
  });

  // Fetch content for this phase
  const { data: content = [], isLoading: contentLoading } = useQuery({
    queryKey: ['baby-development-content', phaseId, locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('baby_development_content')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('locale', locale);

      if (error) throw error;
      return data as BabyDevelopmentContent[];
    },
    enabled: !!phaseId,
    staleTime: 1000 * 60 * 5, // 5 minutes - content rarely changes
  });

  // Fetch activities for this phase
  const { data: activities = [], isLoading: activitiesLoading } = useQuery({
    queryKey: ['baby-development-activities', phaseId, locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('baby_development_activities')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('locale', locale)
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data as BabyDevelopmentActivity[];
    },
    enabled: !!phaseId,
    staleTime: 1000 * 60 * 5, // 5 minutes - activities rarely change
  });

  // Fetch medical info for this phase
  const { data: medical, isLoading: medicalLoading, error } = useQuery({
    queryKey: ['baby-development-medical', phaseId, locale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('baby_development_medical')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('locale', locale)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return (data as BabyDevelopmentMedical) ?? null;
    },
    enabled: !!phaseId,
    staleTime: 1000 * 60 * 5, // 5 minutes - medical info rarely changes
  });

  return {
    categories,
    content,
    activities,
    medical: medical ?? null,
    isLoading: categoriesLoading || contentLoading || activitiesLoading || medicalLoading,
    error: error as Error | null,
  };
}
